import React, { FC } from 'react';
import styled from 'styled-components';
import { Button, TrevorIcon } from '@trevor/ui-components';
import style from '@/css/module/credentials.css';
import { userAction } from '@/util/helpers/ajax-helpers';
import { useNavigate } from 'react-router-dom';
import { useGetDatasourcesQuery } from '@/features/datasources/apiSlice';
import { HerokuQuickSetupStep } from '@/containers/DatasourcesPage/components/ConnectDatasourceSection/HerokuQuickSetupStep';
import { HerokuSetupForm } from '@/containers/DatasourcesPage/components/ConnectDatasourceSection/HerokuQuickSetupForm';
import { useDidMountEffect } from '@/hooks/useDidMountEffect';
import { CONNECT_DATABASE_CONTINUE_NEXT_STEP } from '@/constants/test-ids';
import { AskDatabaseOwnerStep } from './AskDatabaseOwnerStep';
import {
  DATABASE_SETUP_TYPE,
  DATABASE_TYPES,
  DATABASE_TYPES_SETUP_PAGES,
  DatabaseTypeValues,
  isHostingProviderOtherSelected,
} from './utils';
import { DatabaseTypeSelectorStep } from './DatabaseTypeSelectorStep';
import { DatabaseHostingProviderSelectorStep } from './DatabaseHostingProviderSelectorStep';
import { DatabaseSetupTypeSelectorStep } from './DatabaseSetupTypeSelectorStep';

const databaseTypesThatSkipHostingProviderSelection: string[] = [
  DATABASE_TYPES.bigQuery,
  DATABASE_TYPES.redshift,
  DATABASE_TYPES.panoply,
];

const ConnectDatasourceViewContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  gap: var(--spacing-xl);
`;

const ArrowRightIcon = styled(TrevorIcon).attrs({
  name: 'arrow-right',
})`
  margin-left: var(--spacing-s);
`;

type ConnectDatasourceSectionProps = {
  onClose: F0;
  onConnectSampleDatabase: F0;
};
export const ConnectDatasourceSection: FC<ConnectDatasourceSectionProps> = ({
  onClose,
  onConnectSampleDatabase,
}) => {
  const navigate = useNavigate();
  const { data } = useGetDatasourcesQuery();
  const herokuFormRef = React.useRef<HTMLFormElement>(null);

  // This refs are used for the scrolling on section on the previous step
  const step2ContainerRef = React.useRef(null);
  const step3ContainerRef = React.useRef(null);

  const [setupTypeValue, setSetupTypeValue] = React.useState<Maybe<string>>();
  const [databaseTypeValue, setDatabaseTypeValue] =
    React.useState<Maybe<string>>();
  const [herokuSetupValue, setHerokuSetupValue] =
    React.useState<Maybe<string>>();
  const [hostingProviderValue, setHostingProviderValue] =
    React.useState<Maybe<string>>();

  const handleStepStateChange = (
    stateSetter: React.Dispatch<React.SetStateAction<Maybe<string>>>,
    value: string,
  ) => {
    stateSetter((prev) => (prev === value ? null : value));
  };

  const skipHostingProviderSelection =
    databaseTypesThatSkipHostingProviderSelection.includes(
      databaseTypeValue as string,
    );

  const displayStep2 = () => {
    if (setupTypeValue === DATABASE_SETUP_TYPE.heroku) {
      return (
        <HerokuQuickSetupStep
          selectedValue={herokuSetupValue}
          onSelect={(value) =>
            handleStepStateChange(setHerokuSetupValue, value)
          }
        />
      );
    }
    if (setupTypeValue === DATABASE_SETUP_TYPE.helpRequired) {
      return (
        <AskDatabaseOwnerStep
          onConnectSampleDatabase={onConnectSampleDatabase}
        />
      );
    }
    if (setupTypeValue === DATABASE_SETUP_TYPE.custom) {
      return (
        <DatabaseTypeSelectorStep
          selectedValue={databaseTypeValue}
          onSelect={(value) =>
            handleStepStateChange(setDatabaseTypeValue, value)
          }
        />
      );
    }
    return null;
  };

  const handleContinue = () => {
    if (setupTypeValue === DATABASE_SETUP_TYPE.heroku) {
      // Heroku is fired via the form by submit
      // @ts-expect-error: Object is possibly 'null'
      herokuFormRef?.current.submit();
    }

    if (setupTypeValue === DATABASE_SETUP_TYPE.custom) {
      userAction(`connect-database-continue-click`, {
        databaseType: databaseTypeValue,
        hostingProvider: !skipHostingProviderSelection && hostingProviderValue,
      });
      navigate(
        DATABASE_TYPES_SETUP_PAGES[databaseTypeValue as DatabaseTypeValues],
      );
    }
  };

  const displayStep3 = () => {
    const showStep3 =
      setupTypeValue === DATABASE_SETUP_TYPE.custom &&
      databaseTypeValue &&
      !skipHostingProviderSelection;

    if (!showStep3) {
      return null;
    }

    const handleStoreHostingProviderSelectionAction = (value: string) => {
      userAction('connect-database-hosting-provider-option-clicked', {
        option: value,
      });
    };

    const handleOnOtherBlur = () => {
      // Store action on blur if the selected value is dynamic.
      const isDynamicOptionSelected =
        isHostingProviderOtherSelected(hostingProviderValue);
      if (isDynamicOptionSelected && hostingProviderValue) {
        handleStoreHostingProviderSelectionAction(hostingProviderValue);
      }
    };

    const handleSelect = (value: string) => {
      // Store action on select if the selected value is not dynamic.
      const isDynamicOptionSelected = isHostingProviderOtherSelected(value);
      if (!isDynamicOptionSelected && value) {
        handleStoreHostingProviderSelectionAction(value);
      }
      handleStepStateChange(setHostingProviderValue, value);
    };

    return (
      <DatabaseHostingProviderSelectorStep
        selectedValue={hostingProviderValue}
        onOtherBlur={handleOnOtherBlur}
        onSelect={handleSelect}
      />
    );
  };

  const smoothScrollIntoView = (
    scrollToElement: React.MutableRefObject<null>,
  ) => {
    if (scrollToElement) {
      // @ts-expect-error: Object is possibly 'null'.
      scrollToElement.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useDidMountEffect(() => {
    userAction('connect-database-credentials-option-clicked', {
      option: setupTypeValue,
    });

    smoothScrollIntoView(step2ContainerRef);
  }, [setupTypeValue]);

  useDidMountEffect(() => {
    userAction('connect-database-type-option-clicked', {
      option: databaseTypeValue,
    });

    smoothScrollIntoView(step3ContainerRef);
  }, [databaseTypeValue]);

  const showContinueButton =
    herokuSetupValue ||
    hostingProviderValue ||
    databaseTypesThatSkipHostingProviderSelection.includes(
      databaseTypeValue as string,
    );

  return (
    <>
      <ConnectDatasourceViewContainer>
        <DatabaseSetupTypeSelectorStep
          selectedValue={setupTypeValue}
          showHerokuButton={Boolean(data?.isHerokuAddon)}
          onSelect={(value) => handleStepStateChange(setSetupTypeValue, value)}
          onClose={onClose}
        />
        <div ref={step2ContainerRef}>{displayStep2()}</div>
        <div ref={step3ContainerRef}>{displayStep3()}</div>
        {showContinueButton && (
          <Button
            size="large"
            className={style.pulsate}
            onClick={handleContinue}
            data-testid={CONNECT_DATABASE_CONTINUE_NEXT_STEP}
          >
            Continue to next step <ArrowRightIcon />
          </Button>
        )}
      </ConnectDatasourceViewContainer>
      {/* NOTE: The heroku setup with url is made using this form */}
      {herokuSetupValue && (
        <HerokuSetupForm
          ref={herokuFormRef}
          value={herokuSetupValue as string}
        />
      )}
    </>
  );
};
